/* eslint-disable import/no-cycle */
import { Button, FocusTrap } from 'crux';
import React, { useState } from 'react';
import styled, { ThemeProvider, withTheme } from 'styled-components';
import get from 'lodash/get';
import at from 'lodash/at';
import { connect } from 'react-redux';
import FieldSet from '../FieldSet';
import Accordion from '../Accordion';
import { getListTitle } from '../../../redux/selector/listQuestion';
import { getEnableAdd } from '../../../redux/selector/validations';
import {
  resetCurrentEditListIndices,
  setCurrentEditListIndices,
} from '../../../redux/actions/questions';
import { getCSSVariables } from '../../../cruxComponents/utils';
import { UPDATE_LAST_FOCUSED_ELEMENT_ID } from '../../../redux/types';
import { ChevronIcon } from '../Accordion/ChevronIcon';
import { scrollQuestionIntoView } from '../../../utils';

const Body = styled.div`
  padding: ${({ theme: { bodyTheme } }) => (bodyTheme && bodyTheme.padding) || '30px 40px'};
  border-radius: ${({ theme: { bodyTheme, customAccordion } }) =>
    (customAccordion && bodyTheme && bodyTheme.borderRadius) || '8px'};
  ${({ theme: { customAccordion } }) =>
    customAccordion
      ? `
    &:last-of-type {
      margin-bottom: 20px
    }
  `
      : `
      margin-bottom: 20px;
  `};
  position: relative;
  ${({ theme: { isEditMode, customAccordion, bodyTheme } }) =>
    !isEditMode && customAccordion && bodyTheme && bodyTheme.background
      ? `
      &:nth-of-type(2n+1) {
        background: ${bodyTheme.background};
      }
    `
      : `
    background: rgba(255, 255, 255, 1);
    `};
  @media only screen and (max-width: 767px) {
    ${({ theme: { customAccordion } }) => !customAccordion && 'padding: 30px 25px;'}
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 17px 0;
  @media only screen and (max-width: 767px) {
    ${({ theme: { customAccordion } }) =>
      !customAccordion &&
      `
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    `}
  }
`;
const ChevronWrapper = styled.div`
  ${({ theme: { isExpanded } }) =>
    isExpanded ? 'transform: rotate(90deg)' : 'transform: rotate(180deg)'};
  padding: 0 20px;
`;

const Text = styled.div`
  text-align: left;
  overflow-wrap: anywhere;
  ${({ theme: { textTheme } }) =>
    textTheme ||
    `
    font-size: 22px;
    font-weight: 500;
  `}
`;

const ButtonContainer = styled.div`
  ${(listButtonsTheme) => listButtonsTheme && 'display:flex;justify-content: flex-end;'}
  ${({ theme: { isEditMode } }) => isEditMode && 'margin-bottom: 25px;'}
  @media only screen and (max-width: 767px) {
    margin-top: 10px;
  }
`;

const Line = styled.hr`
  border-width: 2px;
  margin: 20px 0;
  ${({ theme: { customAccordion } }) =>
    customAccordion && 'border-color: #262d3f; border-width: 1px; margin: 0'}
`;

const ButtonLink = styled.button`
  border: 0;
  background: transparent;
  font-size: 16px;
  font-weight: bold;
  outline: none;
  &:focus {
    outline: none;
  }
`;

export const EditLink = styled(ButtonLink)`
  ${({
    theme: {
      editLinkTheme,
      editLinkTheme: {
        color,
        fontSize,
        fontWeight,
        fontFamily,
        border,
        borderOnFocus,
        textDecoration,
      } = {},
    },
    disabled,
    isUpdate,
  }) =>
    editLinkTheme
      ? `
    font-size: ${isUpdate ? '18px' : fontSize};
    font-weight: ${fontWeight};
    font-family: ${fontFamily || 'inherit'};
    color: ${disabled ? '#505B5E' : color};
    text-decoration: ${textDecoration || 'none'};
    border: ${border || 'none'};
    &:focus {
      ${borderOnFocus && `border: ${borderOnFocus}`};
    };
  `
      : `
    color: ${disabled ? '#767676' : '5482D5'};
  `}
`;

export const DeleteLink = styled(ButtonLink)`
  ${({
    theme: {
      deleteLinkTheme,
      deleteLinkTheme: {
        color,
        fontSize,
        fontWeight,
        fontFamily,
        border,
        borderOnFocus,
        textDecoration,
      } = {},
    },
    disabled,
    isCancel,
  }) =>
    deleteLinkTheme
      ? `
    font-size: ${isCancel ? '18px' : fontSize};
    font-weight: ${fontWeight};
    font-family: ${fontFamily || 'inherit'};
    color: ${disabled ? '#767676' : color};
    text-decoration: ${textDecoration || 'none'};
    ${isCancel && 'margin-right: 20px'};
    border: ${border || 'none'};
    &:focus {
      ${borderOnFocus && `border: ${borderOnFocus}`};
    };
  `
      : `
    color: ${disabled ? '#767676' : 'rgba(255, 0, 0, 1)'};
  `}
`;

const DeleteCover = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  &:focus {
    ${({ theme: { deleteLinkTheme } }) =>
      deleteLinkTheme &&
      deleteLinkTheme.borderOnFocus &&
      `outline: ${deleteLinkTheme.borderOnFocus}}`};
  }
`;

const Message = styled.div`
  font-size: ${({ theme: { deleteLinkTheme } }) => deleteLinkTheme.fontSize};
  font-weight: 600;
  font-family: ${({ theme: { deleteLinkTheme } }) => deleteLinkTheme.fontFamily};
  color: #262d3f;
  @media only screen and (max-width: 375px) {
    font-size: ${({ theme: { deleteLinkTheme } }) => deleteLinkTheme.fontSize};
  }
`;

const ViewMode = styled.div`
  display: ${({ theme: { isEditMode } }) => (isEditMode ? 'none' : 'inherit')};
`;

const EditMode = styled.div`
  display: ${({ theme: { isEditMode } }) => (isEditMode ? 'inherit' : 'none')};
`;

const ConfirmButtons = styled.div`
  margin-top: 10px;
  width: 100px;
  display: flex;
  justify-content: space-between;
  & button {
    border: 4px solid transparent;
  }
  & button:focus {
    border: 4px solid #037bb5;
  }
`;

function ListItemCard({
  listItem,
  sectionIndex,
  parentListQuestion,
  onEditClick,
  onDeleteClick,
  setEditIndex,
  len,
  index,
  title,
  changeMode,
  isEditMode,
  reflexLevel,
  updateEditIndex,
  isReview,
  listParentId,
  enableUpdate,
  handleCancel,
  hideCancelButton,
  setReflexiveListIndex,
  listId,
  role,
  lastFocusedElementId,
  dispatchNoClick,
  theme: appTheme,
  nestedListProps,
  currentListAnswerIndex,
  handleResetCurrentEditListIndices,
  handleSetCurrentEditListIndices,
  enableOuterActionButton,
  setOuterListActionButton,
}) {
  const [showDeleteCover, setDeleteCover] = useState(false);

  const { pageId, config } = appTheme;
  const theme = get(appTheme, 'config.theme.global');
  const { listButtonsTheme, colorScheme } = theme;
  const {
    body: bodyTheme,
    text: textTheme,
    customAccordion,
    editLinkTheme,
    deleteLinkTheme,
    hideEditDeleteButtons,
    hideAccordion,
  } = get(config, `theme.pageOverrides.${pageId}.listItemCardTheme`, {});

  const cssVariables = getCSSVariables(appTheme);
  const font = at(cssVariables, ['pageOverride.font', 'global.font']);

  if (listButtonsTheme) {
    listButtonsTheme.editButtonTheme.themeConfiguration.font =
      font?.[0]?.['button-list-edit'] || font?.[1]?.['button-list-edit'];
    listButtonsTheme.deleteButtonTheme.themeConfiguration.font =
      font?.[0]?.['button-list-delete'] || font?.[1]?.['button-list-delete'];
  }

  const [expanded, setExpanded] = useState(true);
  const isExpanded = expanded ? 'expanded' : 'collapsed';
  const chevronIconFill = expanded ? '#262d3f' : '#869099';
  const isListEditable = get(parentListQuestion, 'is_editable', true);

  let showListActions = true;
  let isNestedQ = false;
  let parentListIndex = null;
  if (nestedListProps) {
    showListActions = get(nestedListProps, 'showListActions', true);
    isNestedQ = get(nestedListProps, 'isNestedQ', false);
    parentListIndex = get(nestedListProps, 'parentListIndex', null);
  }

  const fieldSetProps = {
    index,
    isNestedQ,
    changeMode,
    listParentId,
    isEditMode,
    nestedListProps,
    parentListIndex,
    fields: listItem,
    isReview,
    sectionIndex,
    updateEditIndex,
    currentListAnswerIndex,
  };

  const hasNestedQ = listItem.filter((q) => q.display_type === 'list').length > 0;
  const shouldDisableOuterActionButton =
    !isNestedQ && hasNestedQ ? !enableOuterActionButton : !enableUpdate;

  /**
   * Handles Edit button in list view mode
   */
  const onClickEditButton = (pListIndex = null, cListIndex = null) => {
    if (!isEditMode) {
      setEditIndex();
      changeMode('listEdit');
      updateEditIndex(index);
      setReflexiveListIndex(-1, '');
      const parentIndex = Number.isInteger(pListIndex) ? pListIndex : null;
      const childListIndex = Number.isInteger(cListIndex) ? cListIndex : null;
      handleSetCurrentEditListIndices(parentIndex, childListIndex, sectionIndex, listParentId);
      if (isNestedQ) {
        setOuterListActionButton(false, sectionIndex);
      }
    } else {
      changeMode('');
      onEditClick();
      setReflexiveListIndex(-1, '');
      handleResetCurrentEditListIndices();
    }
  };

  React.useEffect(() => {
    if (showDeleteCover) {
      const deleteYesEl = document.querySelector('[name="confirm-buttons"] button');
      if (deleteYesEl && typeof deleteYesEl.focus === 'function') {
        deleteYesEl.focus();
      }
    } else if (lastFocusedElementId === `list-delete-cancel-btn-${listId}`) {
      const selector = `div#list-item-card-wrapper-${listId} > div:nth-child(${
        index + 1
      }) > div > div.Accordion.expanded > div > div > button:last-child`;
      const element = document.querySelector(selector);
      if (element) {
        scrollQuestionIntoView({
          element,
          theme: appTheme,
          role,
        });
        element.focus();
      }
    }
  }, [showDeleteCover, lastFocusedElementId]);

  const getEditButton = () => {
    if (listButtonsTheme && listButtonsTheme.editButtonTheme) {
      return (
        <Button
          color={ colorScheme }
          label='edit'
          handleButtonClick={ () => {
            if (isNestedQ) {
              onClickEditButton(parentListIndex, index);
            } else {
              onClickEditButton(index, null);
            }
          } }
          { ...listButtonsTheme.editButtonTheme }
        />
      );
    }
    return (
      <EditLink
        onClick={ () => {
          if (isNestedQ) {
            onClickEditButton(parentListIndex, index);
          } else {
            onClickEditButton(index, null);
          }
        } }
      >
        Edit
      </EditLink>
    );
  };

  /**
   * Handles Delete button in list edit mode
   */
  const onClickDeleteButton = () => {
    if (!isEditMode) {
      setDeleteCover(true);
    } else {
      updateEditIndex(-1);
      setReflexiveListIndex(-1, '');
      handleCancel();
    }
    changeMode('');
    handleResetCurrentEditListIndices();
  };

  const getDeleteButton = () => {
    if (listButtonsTheme && listButtonsTheme.deleteButtonTheme) {
      return (
        <Button
          color={ colorScheme }
          label='delete'
          handleButtonClick={ onClickDeleteButton }
          { ...listButtonsTheme.deleteButtonTheme }
        />
      );
    }
    return <DeleteLink onClick={ onClickDeleteButton }>Delete</DeleteLink>;
  };

  /**
   * Handles Save/Update button in list edit mode
   */
  const onClickUpdateButton = () => {
    changeMode('');
    onEditClick();
    setReflexiveListIndex(-1, '');
  };

  const getUpdateButton = () => {
    return (
      <EditLink
        onClick={ onClickUpdateButton }
        disabled={ isNestedQ ? !enableUpdate : shouldDisableOuterActionButton }
        isUpdate
      >
        Save
      </EditLink>
    );
  };

  /**
   * Handles Cancel button in lsit edit mode
   */
  const onClickCancelButton = () => {
    changeMode('');
    updateEditIndex(-1);
    setReflexiveListIndex(-1, '');
    handleCancel();
  };

  const handleNoClick = () => {
    setDeleteCover(false);
    dispatchNoClick();
  };

  const getCancelButton = () => {
    if (hideCancelButton) return '';
    return (
      <DeleteLink onClick={ onClickCancelButton } isCancel>
        Cancel
      </DeleteLink>
    );
  };

  return (
    <ThemeProvider
      theme={ {
        bodyTheme,
        textTheme,
        customAccordion,
        isEditMode,
        isExpanded: expanded,
        editLinkTheme,
        deleteLinkTheme,
        reflexLevel,
      } }
    >
      <Body name='list-item-card'>
        {showDeleteCover && (
          <FocusTrap modalType='bootstrap'>
            <DeleteCover role='dialog' aria-labelledby='list-dialog-title' name='delete-cover'>
              <Message id='list-dialog-title'>Are you sure you want to delete this?</Message>
              <ConfirmButtons name='confirm-buttons'>
                <DeleteLink onClick={ onDeleteClick }>Yes</DeleteLink>
                <EditLink onClick={ handleNoClick }>No</EditLink>
              </ConfirmButtons>
            </DeleteCover>
          </FocusTrap>
        )}
        <ViewMode name='viewmode'>
          <Header name='header'>
            <Text>{title}</Text>
            {customAccordion ? (
              <ChevronWrapper className='chevron-wrapper'>
                <ChevronIcon
                  fill={ chevronIconFill }
                  className={ isExpanded }
                  onClick={ () => setExpanded(!expanded) }
                  ariaExpanded={ expanded }
                  role={ role }
                  theme={ appTheme }
                  onKeyDown={ (e) => {
                    if (e.key === 'Enter') {
                      setExpanded(!expanded);
                    }
                  } }
                />
              </ChevronWrapper>
            ) : (
              !hideEditDeleteButtons &&
              showListActions &&
              !isReview && (
                <ButtonContainer listButtonsTheme={ listButtonsTheme }>
                  {getEditButton()}
                  {getDeleteButton()}
                </ButtonContainer>
              )
            )}
            <Line />
          </Header>
          {customAccordion ? expanded && <Line /> : <Line />}
          {hideAccordion ? (
            <FieldSet fieldSetProps={ fieldSetProps } />
          ) : (
            <Accordion
              index={ index }
              len={ len }
              title={ getListTitle(listItem) }
              isReview={ isReview }
              customAccordion={ customAccordion }
              expanded={ expanded }
              togglerClassName={ isExpanded }
              toggleExpanded={ () => setExpanded(!expanded) }
              onEditClick={ onClickEditButton }
              setDeleteCover={ setDeleteCover }
              isListEditable={ isListEditable }
              theme={ appTheme }
              role={ role }
            >
              <FieldSet fieldSetProps={ fieldSetProps } />
            </Accordion>
          )}
        </ViewMode>
        <EditMode name='editmode'>
          <FieldSet fieldSetProps={ fieldSetProps } />
          <ButtonContainer listButtonsTheme={ listButtonsTheme }>
            {getCancelButton()}
            {getUpdateButton()}
          </ButtonContainer>
        </EditMode>
      </Body>
    </ThemeProvider>
  );
}

const mapStateToProps = (state, props) => {
  const { sectionIndex, listItem, listQIds } = props;
  const {
    activeSubsectionId,
    editingChildListIndex,
    enableOuterActionButton,
    editingParentListIndex,
  } = get(state, 'questions');
  const requiredListQIds = listItem
    .filter((item) => !['label'].includes(item.question_type))
    .filter((q) => get(q, 'validations.required.value', false))
    .map((item) => item.question_id);
  const enableUpdate = getEnableAdd(
    state,
    sectionIndex,
    activeSubsectionId,
    requiredListQIds,
    listQIds,
  );
  const role = get(state, 'questions.role', '');
  const lastFocusedElementId = get(state, 'questions.lastFocusedElementId', '');
  return {
    enableUpdate,
    role,
    lastFocusedElementId,
    editingChildListIndex,
    enableOuterActionButton,
    editingParentListIndex,
  };
};

const mapDispatchToProps = (dispatch, { listId }) => {
  return {
    handleSetCurrentEditListIndices: (
      parentListIndex,
      childListIndex,
      sectionIndex,
      listParentId,
    ) =>
      dispatch(
        setCurrentEditListIndices(parentListIndex, childListIndex, sectionIndex, listParentId),
      ),
    handleResetCurrentEditListIndices: () => dispatch(resetCurrentEditListIndices()),
    dispatchNoClick: () => {
      dispatch({
        type: UPDATE_LAST_FOCUSED_ELEMENT_ID,
        payload: {
          id: `list-delete-cancel-btn-${listId}`,
        },
      });
    },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(ListItemCard));
